import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, message, Modal } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { formatDistanceToNow } from "date-fns";
import * as Styles from "./styles";
import axios from "../../common/api/axios";
import PageLoader from "../../components/page-loader/PageLoader";
import { ReactComponent as ToastIcon } from "../../assets/icons/toast-icon.svg";
import { formatSlotDisplay } from "../../common/utils/slot";
import {
  buildLocationKey,
  flattenStaticPickWallSlots,
  StaticPickWallSlot,
} from "../../common/utils/staticPickWall";

type TaskType = "decommission" | "purge";

interface BaseRawTask {
  id?: string;
  creationDate?: string;
  isReady?: boolean;
  taskType?: TaskType;
  batchId?: string;
}

interface RawDecommissionTask extends BaseRawTask {
  decommissionBinTask?: {
    binId?: string;
    isReady?: boolean;
    batchId?: string;
  };
}

interface RawPurgeTask extends BaseRawTask {
  purgeBinTask?: {
    binId?: string;
    isReady?: boolean;
    batchId?: string;
  };
}

type RawTask = RawDecommissionTask | RawPurgeTask;

interface SlotLocation {
  x: number;
  y: number;
  z: number;
  position: string;
  pickStationId?: string;
  gateId?: string;
}

interface BaseTask {
  id: string;
  binId: string;
  creationDate: string | null;
  taskType: TaskType;
  batchId: string | null;
}

interface DecommissionTask extends BaseTask {
  slot?: SlotLocation;
}

interface BinItem {
  skuId?: string;
  quantity?: number;
  partition?: string;
  skuBarCode?: string;
  ptlTaskId?: string;
  completionTime?: string;
  timeAdded?: string;
  skuImageUrl?: string;
  imageUrl?: string;
  productName?: string;
  description?: string;
  barCode?: string;
}

interface SkuDetail {
  skuId?: string;
  productName?: string;
  description?: string;
  imageUri?: string;
  barCode?: string;
}

interface DecommissionProps {
  tasks?: RawTask[];
  isLoading?: boolean;
  refreshTasks?: () => Promise<void>;
  onTaskCompleted?: (taskId: string) => void;
}

const HEADERS = {
  Accept: "application/json",
  tenantId: 1,
};

const POLL_DELAYS_MS = [0, 500, 1000, 1500, 2000];

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });

function getPositionKey(x?: number, y?: number, z?: number) {
  if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
    return null;
  }

  return `${x},${y},${z}`;
}

function sortByXDescending(a: string, b: string) {
  const [ax] = a.split(",");
  const [bx] = b.split(",");
  return Number(bx) - Number(ax);
}

function formatRelative(value: string | null) {
  if (!value) {
    return "Unknown";
  }

  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch (_error) {
    return "Unknown";
  }
}

function resolveTaskType(task: RawTask): TaskType {
  if (task.taskType) {
    return task.taskType;
  }

  if ("purgeBinTask" in task && task.purgeBinTask) {
    return "purge";
  }

  return "decommission";
}

function getBinId(task: RawTask): string | undefined {
  if ("purgeBinTask" in task && task.purgeBinTask?.binId) {
    return task.purgeBinTask.binId;
  }

  if ("decommissionBinTask" in task && task.decommissionBinTask?.binId) {
    return task.decommissionBinTask.binId;
  }

  return undefined;
}

function isTaskReady(task: RawTask): boolean {
  if (typeof task.isReady === "boolean") {
    return task.isReady;
  }

  if ("purgeBinTask" in task && typeof task.purgeBinTask?.isReady === "boolean") {
    return Boolean(task.purgeBinTask.isReady);
  }

  if (
    "decommissionBinTask" in task &&
    typeof task.decommissionBinTask?.isReady === "boolean"
  ) {
    return Boolean(task.decommissionBinTask.isReady);
  }

  return false;
}

export default function Decommission({
  tasks: rawTasks = [],
  isLoading: isTasksLoading = false,
  refreshTasks,
  onTaskCompleted,
}: DecommissionProps) {
  const [isCompleting, setIsCompleting] = useState(false);
  const [taskWithSlots, setTaskWithSlots] = useState<DecommissionTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<DecommissionTask | null>(null);
  const [firstRow, setFirstRow] = useState<string[]>([]);
  const [secondRow, setSecondRow] = useState<string[]>([]);
  const [staticSlots, setStaticSlots] = useState<StaticPickWallSlot[]>([]);
  const [isLayoutLoading, setIsLayoutLoading] = useState(true);
  const slotCacheRef = useRef<Record<string, SlotLocation>>({});
  const binItemsCacheRef = useRef<Record<string, BinItem[]>>({});
  const binItemsErrorCacheRef = useRef<Record<string, string | null>>({});
  const binItemsRequestRef = useRef<Record<string, boolean>>({});
  const skuDetailsCacheRef = useRef<Record<string, SkuDetail | null>>({});
  const skuDetailsPromiseRef = useRef<
    Record<string, Promise<SkuDetail | null> | undefined>
  >({});
  const [selectedBinItems, setSelectedBinItems] = useState<BinItem[]>([]);
  const [isBinItemsLoading, setIsBinItemsLoading] = useState(false);
  const [binItemsError, setBinItemsError] = useState<string | null>(null);
  const [completedSlots, setCompletedSlots] = useState<
    Record<string, { binId: string; taskId: string }>
  >({});
  const [completedTaskIds, setCompletedTaskIds] = useState<Record<string, true>>({});
  const previousRawTasksRef = useRef<RawTask[] | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchPickStationsLayout = useCallback(async () => {
    try {
      const response = await axios.get("PickStation/pickStations", {
        headers: HEADERS,
      });

      if (!Array.isArray(response.data)) {
        setFirstRow([]);
        setSecondRow([]);
        return;
      }

      const upper: Set<string> = new Set();
      const lower: Set<string> = new Set();

      response.data.forEach((station: any) => {
        (station?.gates || []).forEach((gate: any) => {
          const slot = gate?.slot;
          const position = getPositionKey(slot?.x, slot?.y, slot?.z);
          if (!position) {
            return;
          }

          if (slot.z === 2) {
            upper.add(position);
          } else if (slot.z === 1) {
            lower.add(position);
          }
        });
      });

      setFirstRow(Array.from(upper).sort(sortByXDescending));
      setSecondRow(Array.from(lower).sort(sortByXDescending));
    } catch (_error) {
      setFirstRow([]);
      setSecondRow([]);
    }
  }, []);

  const fetchStaticPickWallLayout = useCallback(async () => {
    try {
      const response = await axios.get("StaticPickWall", {
        headers: HEADERS,
      });

      setStaticSlots(flattenStaticPickWallSlots(response.data));
    } catch (_error) {
      setStaticSlots([]);
    }
  }, []);

  const locateSlotForBin = useCallback(
    async (binId: string): Promise<SlotLocation | undefined> => {
      if (!binId) {
        return undefined;
      }

      const cached = slotCacheRef.current[binId];
      if (cached) {
        return cached;
      }

      for (const delay of POLL_DELAYS_MS) {
        if (delay > 0) {
          await wait(delay);
        }

        try {
          const response = await axios.get(
            `PickStation/${encodeURIComponent(binId)}/1`,
            {
              headers: HEADERS,
            }
          );

          const pickStation = response.data;
          const gates = Array.isArray(pickStation?.gates) ? pickStation.gates : [];

          for (const gate of gates) {
            const queue = Array.isArray(gate?.processingQueue) ? gate.processingQueue : [];
            if (queue.length === 0) {
              continue;
            }

            const firstItem = queue[0];
            if (firstItem?.binId !== binId) {
              continue;
            }

            const slot = gate?.slot;
            const position = getPositionKey(slot?.x, slot?.y, slot?.z);
            if (!position) {
              continue;
            }

            const locatedSlot: SlotLocation = {
              x: slot.x,
              y: slot.y,
              z: slot.z,
              position,
              pickStationId: pickStation?.pickStationId,
              gateId: gate?.gateId || gate?.id,
            };

            slotCacheRef.current[binId] = locatedSlot;
            return locatedSlot;
          }
        } catch (_error) {
          // Retry until total delay budget is exhausted.
        }
      }

      try {
        const response = await axios.get(`Slot/byAnyBin/${encodeURIComponent(binId)}`, {
          headers: HEADERS,
        });

        const slot = response.data;
        const position = buildLocationKey(slot?.x, slot?.y, slot?.z);
        const isStaticSlot =
          slot?.purpose === "StaticPickWall" ||
          staticSlots.some((staticSlot) => staticSlot.position === position);

        if (position && isStaticSlot) {
          const locatedSlot: SlotLocation = {
            x: slot.x,
            y: slot.y,
            z: slot.z,
            position,
          };

          slotCacheRef.current[binId] = locatedSlot;
          return locatedSlot;
        }
      } catch (_error) {
        // Static bins are not present in the pick-station queue, so a by-bin lookup
        // is used as a fallback only when the dynamic lookup fails.
      }

      return undefined;
    },
    [staticSlots]
  );

  useEffect(() => {
    let isMounted = true;

    const initialise = async () => {
      setIsLayoutLoading(true);
      await Promise.all([fetchPickStationsLayout(), fetchStaticPickWallLayout()]);
      if (isMounted) {
        setIsLayoutLoading(false);
      }
    };

    initialise();

    return () => {
      isMounted = false;
    };
  }, [fetchPickStationsLayout, fetchStaticPickWallLayout]);

  const enrichedTasks = useMemo<BaseTask[]>(() => {
    const tasks: BaseTask[] = [];

    rawTasks.forEach((task) => {
      if (!isTaskReady(task)) {
        return;
      }

      const binId = getBinId(task);
      if (!binId) {
        return;
      }

      tasks.push({
        id: task.id ?? binId,
        binId,
        creationDate: task.creationDate ?? null,
        taskType: resolveTaskType(task),
        batchId:
          task.batchId ??
          ("purgeBinTask" in task ? task.purgeBinTask?.batchId : undefined) ??
          ("decommissionBinTask" in task
            ? task.decommissionBinTask?.batchId
            : undefined) ??
          null,
      });
    });

    return tasks;
  }, [rawTasks]);

  const activeEnrichedTasks = useMemo(
    () => enrichedTasks.filter((task) => !completedTaskIds[task.id]),
    [enrichedTasks, completedTaskIds]
  );

  const mappedTasks = useMemo(
    () => taskWithSlots.filter((task) => Boolean(task.slot)),
    [taskWithSlots]
  );

  const displayFirstRow = useMemo(() => {
    if (firstRow.length > 0) {
      return firstRow;
    }
    return secondRow.length === 0 ? [] : secondRow;
  }, [firstRow, secondRow]);

  const displaySecondRow = useMemo(() => {
    if (firstRow.length > 0 && secondRow.length > 0) {
      return secondRow;
    }
    return [];
  }, [firstRow, secondRow]);

  const staticPositions = useMemo(
    () => staticSlots.map((slot) => slot.position),
    [staticSlots]
  );

  const hasSkuContents = selectedBinItems.length > 0;

  const fetchSkuDetail = useCallback(
    async (skuId: string): Promise<SkuDetail | null> => {
      if (!skuId) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(skuDetailsCacheRef.current, skuId)) {
        return skuDetailsCacheRef.current[skuId];
      }

      if (!skuDetailsPromiseRef.current[skuId]) {
        skuDetailsPromiseRef.current[skuId] = (async () => {
          try {
            const response = await axios.get(
              `MasterData/sku/${encodeURIComponent(skuId)}`,
              {
                headers: HEADERS,
              }
            );

            if (response && typeof response.data === "object" && response.data) {
              const detail = response.data as SkuDetail;
              skuDetailsCacheRef.current[skuId] = detail;
              return detail;
            }

            skuDetailsCacheRef.current[skuId] = null;
            return null;
          } catch (_error) {
            skuDetailsCacheRef.current[skuId] = null;
            return null;
          } finally {
            delete skuDetailsPromiseRef.current[skuId];
          }
        })();
      }

      const pending = skuDetailsPromiseRef.current[skuId];
      if (!pending) {
        return skuDetailsCacheRef.current[skuId] ?? null;
      }

      return pending;
    },
    []
  );

  const handleSelectTask = (task: DecommissionTask) => {
    setSelectedTask(task);
  };

  const handleModalClose = () => {
    setSelectedTask(null);
    setSelectedBinItems([]);
    setBinItemsError(null);
    setIsBinItemsLoading(false);
  };

  const handleCompleteTask = async () => {
    if (!selectedTask) {
      return;
    }

    const taskToComplete = selectedTask;
    const operatorTaskId = taskToComplete.id;
    const isPurgeTask = taskToComplete.taskType === "purge";
    const endpoint = isPurgeTask
      ? `/PickStation/completePurge/${encodeURIComponent(operatorTaskId)}`
      : `/PickStation/completeDecommission/${encodeURIComponent(operatorTaskId)}`;

    setIsCompleting(true);
    try {
      const response = await axios.put(endpoint, {
        headers: HEADERS,
      });

      if (response.status >= 200 && response.status < 300) {
        if (taskToComplete.slot?.position) {
          setCompletedSlots((current) => ({
            ...current,
            [taskToComplete.slot!.position]: {
              binId: taskToComplete.binId,
              taskId: operatorTaskId,
            },
          }));
        }

        setCompletedTaskIds((current) => {
          if (current[operatorTaskId]) {
            return current;
          }

          return {
            ...current,
            [operatorTaskId]: true,
          };
        });

        messageApi.open({
          content: `${isPurgeTask ? "Purged" : "Decommissioned"} tote ${taskToComplete.binId}`,
          icon: <ToastIcon />,
          className: "order",
          style: { marginTop: "85vh" },
        });

        setSelectedTask(null);
        setSelectedBinItems([]);
        setBinItemsError(null);
        setIsBinItemsLoading(false);
        if (slotCacheRef.current[taskToComplete.binId]) {
          delete slotCacheRef.current[taskToComplete.binId];
        }

        if (onTaskCompleted) {
          onTaskCompleted(operatorTaskId);
        }

        if (refreshTasks) {
          await refreshTasks();
        } else {
          setTaskWithSlots((current) =>
            current.filter((task) => task.id !== taskToComplete.id)
          );
        }
      } else {
        throw new Error("Failed to complete decommission task");
      }
    } catch (_error) {
      messageApi.open({
        type: "error",
        content: `Unable to complete ${isPurgeTask ? "purge" : "decommission"} for ${operatorTaskId}`,
        className: "error",
        style: { marginTop: "85vh" },
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const REGULAR_SLOT_CARD_HEIGHT = "280px";
  const STATIC_SLOT_CARD_HEIGHT = "180px";
  const STATIC_SLOT_CARD_WIDTH = "140px";

  const renderSlotCard = (position: string, isStatic: boolean = false) => {
    const task = taskWithSlots.find((item) => item.slot?.position === position);
    const isSelected = Boolean(task && selectedTask?.id === task.id);
    const completedSlot = completedSlots[position];
    const isCompleted = Boolean(completedSlot);
    const isOccupied = Boolean(task) || isCompleted;
    const isSelectable = Boolean(task) && !isCompleted;
    const displayBinId = (() => {
      if (completedSlot) {
        return completedSlot.binId;
      }

      if (task) {
        const typeLabel = task.taskType === "purge" ? "Purge" : "Decommission";
        return `${task.binId} (${typeLabel})`;
      }

      return "";
    })();

    return (
      <Styles.SlotCardWrapper
        key={position}
        $isSelectable={isSelectable}
        onClick={() => {
          if (isSelectable && task) {
            handleSelectTask(task);
          }
        }}
      >
        <Styles.SlotCardSurface
          $isCompleted={isCompleted}
          $isOccupied={isOccupied}
          $isSelected={isSelected}
          $isSelectable={isSelectable}
          style={{
            height: isStatic ? STATIC_SLOT_CARD_HEIGHT : REGULAR_SLOT_CARD_HEIGHT,
          }}
        >
          <Styles.SlotCardBody $isCompleted={isCompleted} $isOccupied={isOccupied}>
            {displayBinId}
          </Styles.SlotCardBody>
          <Styles.SlotCardFooter $isCompleted={isCompleted}>
            <span>Bin - {formatSlotDisplay(position)}</span>
          </Styles.SlotCardFooter>
        </Styles.SlotCardSurface>
      </Styles.SlotCardWrapper>
    );
  };

  useEffect(() => {
    const taskType = selectedTask?.taskType;
    const binId = selectedTask?.binId;
    const taskId = selectedTask?.id;

    if (!taskId || taskType !== "purge") {
      setSelectedBinItems([]);
      setBinItemsError(null);
      setIsBinItemsLoading(false);
      return;
    }

    if (!binId) {
      setSelectedBinItems([]);
      setBinItemsError("Bin details unavailable.");
      setIsBinItemsLoading(false);
      return;
    }

    const cachedItems = binItemsCacheRef.current[binId];
    const cachedError = binItemsErrorCacheRef.current[binId] ?? null;

    if (cachedItems !== undefined) {
      setSelectedBinItems(cachedItems);
      setBinItemsError(cachedError);
      setIsBinItemsLoading(false);
      return;
    }

    if (binItemsRequestRef.current[binId]) {
      setIsBinItemsLoading(true);
      setBinItemsError(null);
      return;
    }

    let isActive = true;

    const fetchItems = async () => {
      binItemsRequestRef.current[binId] = true;
      setIsBinItemsLoading(true);
      setBinItemsError(null);

      try {
        const response = await axios.get(
          `BinMaster/items/${encodeURIComponent(binId)}`,
          {
            headers: HEADERS,
          }
        );

        if (!isActive) {
          return;
        }

        const items = Array.isArray(response.data) ? response.data : [];
        const enriched = await Promise.all(
          items.map(async (item: BinItem) => {
            const skuId = item.skuId;
            if (!skuId) {
              return item;
            }

            const detail = await fetchSkuDetail(skuId);
            if (!detail) {
              return item;
            }

            return {
              ...item,
              productName:
                detail.productName ?? detail.description ?? item.productName ?? item.partition,
              description: detail.description ?? item.description,
              skuImageUrl: detail.imageUri ?? item.skuImageUrl,
              imageUrl: detail.imageUri ?? item.imageUrl,
              skuBarCode: detail.barCode ?? item.skuBarCode,
              barCode: detail.barCode ?? item.barCode,
            };
          })
        );

        binItemsCacheRef.current[binId] = enriched;
        binItemsErrorCacheRef.current[binId] = null;
        setSelectedBinItems(enriched);
        setBinItemsError(null);
      } catch (_error) {
        if (!isActive) {
          return;
        }

        binItemsCacheRef.current[binId] = [];
        binItemsErrorCacheRef.current[binId] = "Unable to load SKU details.";
        setSelectedBinItems([]);
        setBinItemsError("Unable to load SKU details.");
      } finally {
        if (isActive) {
          setIsBinItemsLoading(false);
        }

        binItemsRequestRef.current[binId] = false;
      }
    };

    fetchItems();

    return () => {
      isActive = false;
    };
  }, [selectedTask?.binId, selectedTask?.taskType, selectedTask?.id, fetchSkuDetail]);

  useEffect(() => {
    let isMounted = true;

    const mapTasksToSlots = async () => {
      if (isLayoutLoading) {
        return;
      }

      const mapped = await Promise.all(
        activeEnrichedTasks.map(async (task) => {
          const slot = await locateSlotForBin(task.binId);
          return {
            ...task,
            slot,
          };
        })
      );

      if (!isMounted) {
        return;
      }

      setTaskWithSlots(mapped);
    };

    mapTasksToSlots();

    return () => {
      isMounted = false;
    };
  }, [activeEnrichedTasks, locateSlotForBin, isLayoutLoading]);

  useEffect(() => {
    if (taskWithSlots.length === 0) {
      if (selectedTask) {
        setSelectedTask(null);
      }
      return;
    }

    if (!selectedTask) {
      return;
    }

    const stillExists = taskWithSlots.find((task) => task.id === selectedTask.id);
    if (stillExists !== selectedTask) {
      setSelectedTask(stillExists ?? null);
    }
  }, [taskWithSlots, selectedTask]);

  useEffect(() => {
    const previous = previousRawTasksRef.current;
    if (previous && previous !== rawTasks && Object.keys(completedSlots).length > 0) {
      setCompletedSlots({});
    }

    previousRawTasksRef.current = rawTasks;
  }, [rawTasks, completedSlots]);

  useEffect(() => {
    if (Object.keys(completedTaskIds).length === 0) {
      return;
    }

    const activeIds = new Set(
      rawTasks
        .map((task) => task.id ?? getBinId(task))
        .filter((value): value is string => Boolean(value))
    );

    const remainingEntries = Object.entries(completedTaskIds).filter(([taskId]) =>
      activeIds.has(taskId)
    );

    if (remainingEntries.length !== Object.keys(completedTaskIds).length) {
      const next = remainingEntries.reduce<Record<string, true>>((accumulator, [taskId]) => {
        accumulator[taskId] = true;
        return accumulator;
      }, {});

      setCompletedTaskIds(next);
    }
  }, [rawTasks, completedTaskIds]);

  const isLoadingState = isTasksLoading || isLayoutLoading;

  const modalWidth =
    selectedTask && selectedTask.taskType === "purge" && hasSkuContents ? 1020 : 760;

  return (
    <Styles.Container>
      {contextHolder}

      <Styles.ContentLayout $singleColumn={true}>
        <Styles.SlotsColumn>
          <Styles.SectionTitle>Active Slots</Styles.SectionTitle>

          {displayFirstRow.length > 0 ? (
            <Styles.SlotsRow $columns={displayFirstRow.length}>
              {displayFirstRow.map((position) => renderSlotCard(position))}
            </Styles.SlotsRow>
          ) : null}

          {displaySecondRow.length > 0 ? (
            <Styles.SlotsRow $columns={displaySecondRow.length}>
              {displaySecondRow.map((position) => renderSlotCard(position))}
            </Styles.SlotsRow>
          ) : null}

          {staticPositions.length > 0 ? (
            <>
              <Styles.SectionTitle>Static Pickwall</Styles.SectionTitle>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  width: "100%",
                  padding: "0 12px",
                  overflowX: "auto",
                }}
              >
                {staticPositions.map((position) => (
                  <div
                    key={position}
                    style={{
                      flex: `0 0 ${STATIC_SLOT_CARD_WIDTH}`,
                      width: STATIC_SLOT_CARD_WIDTH,
                      minWidth: STATIC_SLOT_CARD_WIDTH,
                    }}
                  >
                    {renderSlotCard(position, true)}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </Styles.SlotsColumn>
      </Styles.ContentLayout>

      <Modal
        title=""
        visible={Boolean(selectedTask)}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
        width={modalWidth}
      >
        {selectedTask && (
          <Styles.ModalContent
            $hasSkuImages={selectedTask.taskType === "purge" && hasSkuContents}
          >
            <Styles.ToteSection>
              <Styles.SectionTitle>Tote Information</Styles.SectionTitle>
              <Styles.InfoRow>
                <span>Bin ID</span>
                <span>{selectedTask.binId}</span>
              </Styles.InfoRow>
              <Styles.InfoRow>
                <span>Slot</span>
                <span>
                  {selectedTask.slot
                    ? formatSlotDisplay(selectedTask.slot.position)
                    : "Pending mapping"}
                </span>
              </Styles.InfoRow>
              <Styles.InfoRow>
                <span>Task Type</span>
                <span>{selectedTask.taskType === "purge" ? "Purge" : "Decommission"}</span>
              </Styles.InfoRow>
              <Styles.InfoRow>
                <span>Batch ID</span>
                <span>{selectedTask.batchId || "N/A"}</span>
              </Styles.InfoRow>
              <Styles.InfoRow>
                <span>Created</span>
                <span>{formatRelative(selectedTask.creationDate)}</span>
              </Styles.InfoRow>

              {selectedTask.taskType === "purge" ? (
                <>
                  {hasSkuContents ? (
                    <Styles.ContentsSection>
                      <Styles.SubSectionHeading>Contents</Styles.SubSectionHeading>
                      <Styles.ContentsList>
                        {selectedBinItems.map((item, index) => {
                          const skuCode = item.skuId ?? "SKU";
                          const skuName =
                            item.productName ||
                            item.description ||
                            item.partition ||
                            item.skuBarCode ||
                            "SKU Item";
                          const skuKey = `${skuCode}-${item.partition ?? index}`;
                          const quantity =
                            typeof item.quantity === "number" ? item.quantity : 0;
                          const displayId =
                            item.barCode || item.skuBarCode || item.partition || skuCode;

                          return (
                            <Styles.ContentItem key={skuKey}>
                              <Styles.ContentInfo>
                                <Styles.ContentTitle>{skuName}</Styles.ContentTitle>
                                <Styles.ContentId>{displayId}</Styles.ContentId>
                              </Styles.ContentInfo>
                              <Styles.ContentQuantity>
                                <Styles.ContentQuantityValue>{quantity}</Styles.ContentQuantityValue>
                                <Styles.ContentQuantityLabel>qty</Styles.ContentQuantityLabel>
                              </Styles.ContentQuantity>
                            </Styles.ContentItem>
                          );
                        })}
                      </Styles.ContentsList>
                    </Styles.ContentsSection>
                  ) : null}

                  {!hasSkuContents && isBinItemsLoading ? (
                    <Styles.ContentsPlaceholder>Loading contents...</Styles.ContentsPlaceholder>
                  ) : null}

                  {!hasSkuContents && binItemsError ? (
                    <Styles.ContentsPlaceholder>{binItemsError}</Styles.ContentsPlaceholder>
                  ) : null}
                </>
              ) : null}
            </Styles.ToteSection>

            {selectedTask.taskType === "purge" && hasSkuContents ? (
              <Styles.SkuSection>
                <Styles.SubSectionHeading>SKU Images</Styles.SubSectionHeading>
                <Styles.SkuGrid>
                  {selectedBinItems.map((item, index) => {
                    const skuCode = item.skuId ?? "SKU";
                    const skuName =
                      item.productName ||
                      item.description ||
                      item.partition ||
                      item.skuBarCode ||
                      "SKU Item";
                    const skuKey = `${skuCode}-${item.partition ?? index}`;
                    const imageUrl = item.skuImageUrl || item.imageUrl;

                    return (
                      <Styles.SkuCard key={skuKey}>
                        <Styles.SkuCardImage $imageUrl={imageUrl}>
                          {imageUrl ? (
                            <Styles.SkuCardCodeBadge>{skuCode}</Styles.SkuCardCodeBadge>
                          ) : (
                            <Styles.SkuCardPlaceholder>{skuCode}</Styles.SkuCardPlaceholder>
                          )}
                        </Styles.SkuCardImage>
                        <Styles.SkuCardFooter>
                          <Styles.SkuCardFooterName>{skuName}</Styles.SkuCardFooterName>
                        </Styles.SkuCardFooter>
                      </Styles.SkuCard>
                    );
                  })}
                </Styles.SkuGrid>
              </Styles.SkuSection>
            ) : null}

            <Styles.ActionSection>
              <Styles.ActionHeader>
                <Styles.ActionTitle>Action Required</Styles.ActionTitle>
                <Styles.ActionDivider />
              </Styles.ActionHeader>
              <Styles.ActionCard>
                <Styles.ActionPrompt>Remove tote</Styles.ActionPrompt>
                <Styles.ActionArrow>
                  <ArrowRightOutlined />
                </Styles.ActionArrow>
              </Styles.ActionCard>
              <Styles.ActionDescription>
                Remove tote from its location.
              </Styles.ActionDescription>
              <Styles.ActionButtons>
                <Button
                  type="primary"
                  danger
                  block
                  loading={isCompleting}
                  onClick={handleCompleteTask}
                >
                  {selectedTask.taskType === "purge" ? "Complete Purge" : "Decommission"}
                </Button>
              </Styles.ActionButtons>
              <Styles.ActionFooter>
                Acknowledge only after removing the tote.
              </Styles.ActionFooter>
            </Styles.ActionSection>
          </Styles.ModalContent>
        )}
      </Modal>
    </Styles.Container>
  );
}
